import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type Option = {
  label: string;
  value: string | any;
};

interface MultiSelectProps {
  selectedOptions: Option[];
  options: Option[];
  onSearch: (search: string) => void;
  onOptionSelect: (option: Option) => void;
  onOptionRemove: (option: Option) => void;
  onAddNewOption: (label: string) => void;
  renderOption?: (option: Option) => React.ReactNode;
  placeholder?: string;
  // Legacy props accepted but ignored — state is now managed internally
  isOpen?: boolean;
  searchTerm?: string;
  toggleDropdown?: () => void;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  selectedOptions,
  options,
  onSearch,
  onOptionSelect,
  onOptionRemove,
  onAddNewOption,
  renderOption,
  placeholder = "Select options...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const calcPos = () => {
    if (!containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: r.width });
  };

  const open = () => {
    calcPos();
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setSearch("");
    onSearch("");
  };

  const handleSearchChange = (val: string) => {
    setSearch(val);
    onSearch(val);
    if (!isOpen) open();
  };

  const handleSelect = (option: Option) => {
    onOptionSelect(option);
    setSearch("");
    onSearch("");
    inputRef.current?.focus();
  };

  const handleAddNew = () => {
    onAddNewOption(search);
    setSearch("");
    onSearch("");
  };

  // Close when clicking outside both the trigger and the portal dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t) || dropdownRef.current?.contains(t)) return;
      close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  // Keep dropdown aligned on scroll / resize
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => calcPos();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="w-full">
      {/* Chip container + inline search — clicking anywhere focuses the input */}
      <div
        className="flex flex-wrap gap-1.5 px-2 py-1.5 min-h-[38px] border border-gray-300 rounded-md cursor-text focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent mt-2"
        onMouseDown={(e) => {
          if (e.target !== inputRef.current) {
            e.preventDefault();
            inputRef.current?.focus();
          }
        }}
        onClick={open}
      >
        {selectedOptions.map((option) => (
          <span
            key={option.value}
            className="flex items-center gap-0.5 px-2 py-0.5 text-sm text-blue-700 bg-blue-100 rounded"
          >
            {option.label}
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onOptionRemove(option); }}
              className="ml-0.5 text-blue-400 hover:text-blue-700 leading-none font-medium"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={open}
          onKeyDown={(e) => e.key === "Escape" && close()}
          className="flex-1 min-w-[80px] outline-none border-0 text-sm bg-transparent py-0.5"
          placeholder={selectedOptions.length === 0 ? placeholder : ""}
        />
      </div>

      {/* Portal dropdown — rendered at document.body so it's never clipped by
          parent overflow:hidden or a lower stacking context */}
      {isOpen &&
        createPortal(
          <ul
            ref={dropdownRef}
            className="bg-white border border-gray-200 rounded-md shadow-lg overflow-y-auto max-h-48 py-1 m-0 list-none"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: 9999,
            }}
          >
            {options.map((option) => (
              <li
                key={option.value}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(option); }}
                className="px-4 py-1.5 text-sm capitalize cursor-pointer hover:bg-blue-50"
              >
                {renderOption ? renderOption(option) : option.label}
              </li>
            ))}
            {options.length === 0 && (
              <li className="px-4 py-2 text-sm text-gray-400">
                {search ? "No results found" : "Type to search..."}
              </li>
            )}
            {search && !options.some((o) => o.label.toLowerCase() === search.toLowerCase()) && (
              <li
                onMouseDown={(e) => { e.preventDefault(); handleAddNew(); }}
                className="px-4 py-1.5 text-sm text-blue-600 cursor-pointer hover:bg-blue-50 border-t border-gray-100"
              >
                Add "{search}"
              </li>
            )}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default MultiSelect;
